"use client";

import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import InputGroup from "react-bootstrap/InputGroup";
import Button from "react-bootstrap/Button";
import { usePowerSearch } from "./contexts/power-search-context";
import Card from "react-bootstrap/Card";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useFullScreen } from "./contexts/FullScreenProvider";
import { mixpanelTrack } from "shared/trackEvents/mixpanelTracking";
import { isOpenRemunerationPlan } from "shared/plans";
import {
  POWER_SEARCH_EVENT_NAMES,
  MIXPANEL_PROPS,
} from "shared/trackEvents/constants";
import debounce from "lodash/debounce";

// Debounced Mixpanel tracking (outside component to persist across renders)
const debouncedMixpanelTrack = debounce((eventName, value, userEmail) => {
  mixpanelTrack(eventName, {
    [MIXPANEL_PROPS.SEARCH_TERM]: value,
    userEmail,
  });
}, 500);

const SearchInputGroup = () => {
  const {
    keyword,
    matchPhrase,
    directorNameContains,
    updateFilter,
    handleSearchSubmit,
    searchResultsLoading,
    currentUser,
  } = usePowerSearch();

  const { isFullScreen } = useFullScreen();

  const _handleExactKeywordClick = (checked) => {
    if (currentUser) {
      const isOpenRemUser = isOpenRemunerationPlan(currentUser);
      const eventName = isOpenRemUser
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_EXACT_KEYWORD_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_EXACT_KEYWORD_CLICKED;

      mixpanelTrack(eventName, {
        [MIXPANEL_PROPS.OPTION]: checked ? "enabled" : "disabled",
        userEmail: currentUser?.email,
      });
    }

    updateFilter("matchPhrase", checked);
  };

  const _handleKeywordInputChange = (value) => {
    if (currentUser && value) {
      const isOpenRemUser = isOpenRemunerationPlan(currentUser);
      const eventName = isOpenRemUser
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_INPUT_SEARCH_SELECTED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_INPUT_SEARCH_SELECTED;

      debouncedMixpanelTrack(eventName, value, currentUser?.email);
    }

    updateFilter("keyword", value);
  };

  const _handleSearchButtonClick = () => {
    if (currentUser) {
      const isOpenRemUser = isOpenRemunerationPlan(currentUser);
      const eventName = isOpenRemUser
        ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_SEARCH_BUTTON_CLICKED
        : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_SEARCH_BUTTON_CLICKED;

      mixpanelTrack(eventName, {
        [MIXPANEL_PROPS.SEARCH_TERM]: keyword,
        [MIXPANEL_PROPS.OPTION]: matchPhrase ? "exact_match" : "keyword_search",
        userEmail: currentUser?.email,
      });
    }

    handleSearchSubmit();
  };

  return (
    <>
      <div className="search-inputs-modern">
        {/* Keyword Search Row */}
        <Row className="g-2">
          <Col xs={12}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Form.Label
                htmlFor="keywordSearch"
                className="fw-bold small"
                style={{ margin: 0, padding: 0 }}
              >
                Keyword Search
              </Form.Label>
              <div className="d-flex align-items-center">
                <Form.Check
                  type="checkbox"
                  id="matchPhrase"
                  checked={matchPhrase}
                  onChange={(e) => _handleExactKeywordClick(e.target.checked)}
                  className="small"
                  style={{ margin: 0, padding: 0 }}
                />
                <span className="small">
                  Exact Words
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip
                        id="exact-words-tooltip"
                        className={isFullScreen ? "tooltip-fullscreen" : ""}
                      >
                        Only return results that match the exact words you
                        enter.
                      </Tooltip>
                    }
                  >
                    <i
                      className="fa fa-question-circle text-muted  ml-1 small"
                      style={{ cursor: "pointer" }}
                    />
                  </OverlayTrigger>
                </span>
              </div>
            </div>

            <div className="mb-2">
              <InputGroup>
                <Form.Control
                  id="keywordSearch"
                  placeholder="Enter keywords (e.g., industry, skill)"
                  value={keyword}
                  onChange={(e) => _handleKeywordInputChange(e.target.value)}
                  className="power-search-input"
                  style={{ fontSize: "11px" }}
                />
                <Button
                  variant="primary"
                  type="submit"
                  disabled={searchResultsLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    _handleSearchButtonClick();
                  }}
                  style={{
                    fontSize: "11px",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom: "none",
                    borderRight: "none",
                    borderTopLeftRadius: "0",
                    borderBottomLeftRadius: "0",
                    color: "#fff",
                    backgroundColor: "#1E6685",
                  }}
                >
                  Search
                </Button>
              </InputGroup>
            </div>
          </Col>
        </Row>
        <div className="text-muted small mt-1">
          <i className="fa fa-info-circle mr-1 small" />
          <span className="small">
            Use keyword search for general terms or name for specific people.
            Use filters below to narrow down results by specific criteria.
          </span>
        </div>
        <hr className="my-3" />
      </div>
    </>
  );
};

export default SearchInputGroup;
